import React, { useState } from 'react';
import { X, Lock, Eye, EyeOff } from 'lucide-react';
import { Button, Input } from '../ui';
import { userService } from '../../services/userService';
import { toast } from '../ui/Toaster';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [formData, setFormData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  if (!isOpen) {
    return null;
  }

  const handleInputChange = (field: keyof PasswordFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = 'Huidig wachtwoord is verplicht';
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'Nieuw wachtwoord is verplicht';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Nieuw wachtwoord moet minimaal 8 karakters lang zijn';
    } else if (!/[A-Z]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Nieuw wachtwoord moet minimaal één hoofdletter bevatten';
    } else if (!/[a-z]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Nieuw wachtwoord moet minimaal één kleine letter bevatten';
    } else if (!/[0-9]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Nieuw wachtwoord moet minimaal één cijfer bevatten';
    } else if (!/[^A-Za-z0-9]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Nieuw wachtwoord moet minimaal één speciaal teken bevatten';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Bevestig je nieuwe wachtwoord';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Nieuw wachtwoord en bevestiging komen niet overeen';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'Nieuw wachtwoord moet anders zijn dan het huidige wachtwoord';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      
      await userService.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
      
      toast.success('Succes!', 'Wachtwoord succesvol gewijzigd');
      
      // Reset form and close modal
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setErrors({});
      onClose();
    } catch (error: any) {
      console.error('Failed to change password:', error);
      toast.error('Fout', error.message || 'Kon wachtwoord niet wijzigen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
    setShowPasswords({
      current: false,
      new: false,
      confirm: false,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-canvas/70 backdrop-blur-sm transition-opacity"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-surface border border-white/[.08] rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-panel transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Lock className="h-6 w-6 text-neon-cyan mr-2" />
              <h3 className="text-lg font-bold text-fg-bright">
                Wachtwoord wijzigen
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="text-fg-muted hover:text-fg-bright transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current Password */}
            <div className="relative">
              <Input
                label="Huidig wachtwoord"
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                error={errors.currentPassword}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-10 text-fg-faint hover:text-fg-soft transition-colors"
              >
                {showPasswords.current ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* New Password */}
            <div className="relative">
              <Input
                label="Nieuw wachtwoord"
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                error={errors.newPassword}
                required
                helperText="Minimaal 8 karakters met hoofdletter, kleine letter, cijfer en speciaal teken"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-10 text-fg-faint hover:text-fg-soft transition-colors"
              >
                {showPasswords.new ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <Input
                label="Bevestig nieuw wachtwoord"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                error={errors.confirmPassword}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-10 text-fg-faint hover:text-fg-soft transition-colors"
              >
                {showPasswords.confirm ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1"
              >
                Annuleren
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isLoading}
                className="flex-1"
              >
                Wachtwoord wijzigen
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;